import {DeepReadonly, readonly, ref, Ref} from 'vue'
import {LiveQueryResult, LiveQueryService as ILiveQueryService, LiveQueryTableResult, LiveQueryTableRow} from '@hantera/portal-app/src/composition/use-live-queries'
import {WebSocketClient} from './websocket-client'
import {parseResponseJson} from '@/backend'
import Decimal from 'decimal.js'
import log from './log'

interface ActiveNodeQuery {
  id: string
  spec: object
  nodes: Ref<unknown[]>
  loading: Ref<boolean>
  totalCount: Ref<number | null>
  capped: Ref<boolean>
  revision: Ref<number>
  error: Ref<{ code: string; message: string } | null>
  nodeMap: Map<string, unknown>
}

interface LiveQueryTableOrderBy {
  column: string
  direction: 'asc' | 'desc'
}

interface LiveQueryBatchEntry {
  action: 'added' | 'updated' | 'removed'
  nodeId: string
  data?: unknown
}

interface ActiveTableQuery {
  id: string
  spec: object
  columns: Ref<string[]>
  rows: Ref<LiveQueryTableRow[]>
  orderBy: LiveQueryTableOrderBy[]
  loading: Ref<boolean>
  totalCount: Ref<number | null>
  capped: Ref<boolean>
  revision: Ref<number>
  error: Ref<{ code: string; message: string } | null>
  rowMap: Map<string, LiveQueryTableRow[]>
}

export class LiveQueryService implements ILiveQueryService {
  private nodeQueries = new Map<string, ActiveNodeQuery>()
  private tableQueries = new Map<string, ActiveTableQuery>()
  private idCounter = 0

  constructor(private readonly client: WebSocketClient) {
    client.on('liveQueryData', (msg) => this.handleLiveQueryData(msg))
    client.on('liveQueryAddedNode', (msg) => this.handleAddedNode(msg))
    client.on('liveQueryUpdatedNode', (msg) => this.handleUpdatedNode(msg))
    client.on('liveQueryRemovedNode', (msg) => this.handleRemovedNode(msg))
    client.on('liveQueryBatch', (msg) => this.handleBatch(msg))
    client.on('error', (msg) => this.handleError(msg))
    client.onAuthenticated(() => this.recreateAll())
  }

  query<T = unknown>(spec: object): LiveQueryResult<T> {
    const id = `lq-${++this.idCounter}`

    const nodes = ref<unknown[]>([])
    const loading = ref(true)
    const totalCount = ref<number | null>(null)
    const capped = ref(false)
    const revision = ref(0)
    const error = ref<{ code: string; message: string } | null>(null)

    const active: ActiveNodeQuery = { id, spec, nodes, loading, totalCount, capped, revision, error, nodeMap: new Map() }
    this.nodeQueries.set(id, active)

    if (this.client.state.value === 'connected') {
      this.client.send({ type: 'createLiveQuery', id, query: spec })
    }

    return {
      nodes: readonly(nodes) as DeepReadonly<Ref<T[]>>,
      loading: readonly(loading),
      totalCount: readonly(totalCount),
      capped: readonly(capped),
      revision: readonly(revision),
      error: readonly(error),
      dispose: () => {
        this.nodeQueries.delete(id)
        if (this.client.state.value === 'connected') {
          this.client.send({ type: 'destroyLiveQuery', id })
        }
      }
    }
  }

  queryTable(spec: object): LiveQueryTableResult {
    const id = `lq-${++this.idCounter}`

    const columns = ref<string[]>([])
    const rows = ref<LiveQueryTableRow[]>([])
    const loading = ref(true)
    const totalCount = ref<number | null>(null)
    const capped = ref(false)
    const revision = ref(0)
    const error = ref<{ code: string; message: string } | null>(null)

    const active: ActiveTableQuery = { id, spec, columns, rows, orderBy: [], loading, totalCount, capped, revision, error, rowMap: new Map() }
    this.tableQueries.set(id, active)

    if (this.client.state.value === 'connected') {
      this.client.send({ type: 'createLiveQuery', id, query: spec, format: 'table' })
    }

    return {
      columns: readonly(columns),
      rows: readonly(rows),
      loading: readonly(loading),
      totalCount: readonly(totalCount),
      capped: readonly(capped),
      revision: readonly(revision),
      error: readonly(error),
      dispose: () => {
        this.tableQueries.delete(id)
        if (this.client.state.value === 'connected') {
          this.client.send({ type: 'destroyLiveQuery', id })
        }
      }
    }
  }

  private handleLiveQueryData(msg: Record<string, unknown>): void {
    const id = msg.id as string
    const hasMore = (msg.hasMore as boolean) ?? false

    const nodeQuery = this.nodeQueries.get(id)
    if (nodeQuery) {
      const data = (msg.data as Record<string, unknown>[]) ?? []
      for (const raw of data) {
        const item = this.parseNode(raw)
        const nodeId = item.nodeId as string | undefined
        if (nodeId) nodeQuery.nodeMap.set(nodeId, item)
        nodeQuery.nodes.value.push(item)
      }

      if (!hasMore) {
        nodeQuery.loading.value = false
        nodeQuery.totalCount.value = (msg.totalCount as number | undefined) ?? nodeQuery.nodes.value.length
        nodeQuery.capped.value = (msg.capped as boolean) ?? false
      }
      return
    }

    const tableQuery = this.tableQueries.get(id)
    if (!tableQuery) return

    const data = msg.data as { columns?: string[], orderBy?: LiveQueryTableOrderBy[], rows?: Array<{ nodeId: string, values: unknown[] }> }
    if (data?.columns) tableQuery.columns.value = data.columns
    if (data?.orderBy) tableQuery.orderBy = data.orderBy
    if (data?.rows) {
      for (const row of data.rows) {
        this.appendTableRow(tableQuery, row.nodeId, row.values)
      }
      this.sortTableRows(tableQuery)
    }

    if (!hasMore) {
      tableQuery.loading.value = false
      tableQuery.totalCount.value = (msg.totalCount as number | undefined) ?? tableQuery.rowMap.size
      tableQuery.capped.value = (msg.capped as boolean) ?? false
    }
  }

  private handleAddedNode(msg: Record<string, unknown>): void {
    this.applyEntries(msg.id as string, [
      { action: 'added', nodeId: msg.nodeId as string, data: msg.data }
    ])
  }

  private handleUpdatedNode(msg: Record<string, unknown>): void {
    this.applyEntries(msg.id as string, [
      { action: 'updated', nodeId: msg.nodeId as string, data: msg.data }
    ])
  }

  private handleRemovedNode(msg: Record<string, unknown>): void {
    this.applyEntries(msg.id as string, [
      { action: 'removed', nodeId: msg.nodeId as string }
    ])
  }

  private handleBatch(msg: Record<string, unknown>): void {
    const updates = (msg.updates as LiveQueryBatchEntry[]) ?? []
    if (updates.length === 0) return

    this.applyEntries(msg.id as string, updates)
  }

  /**
   * Applies every entry before touching `revision`, so a batch renders as one frame rather than
   * flashing the intermediate states (notably a capped window's remove-then-add swap).
   */
  private applyEntries(id: string, entries: LiveQueryBatchEntry[]): void {
    const nodeQuery = this.nodeQueries.get(id)
    if (nodeQuery) {
      for (const entry of entries) this.applyNodeEntry(nodeQuery, entry)
      nodeQuery.revision.value++
      return
    }

    const tableQuery = this.tableQueries.get(id)
    if (!tableQuery) return

    for (const entry of entries) this.applyTableEntry(tableQuery, entry)
    this.sortTableRows(tableQuery)
    tableQuery.revision.value++
  }

  private applyNodeEntry(query: ActiveNodeQuery, entry: LiveQueryBatchEntry): void {
    const nodes = query.nodes.value as Record<string, unknown>[]
    const index = nodes.findIndex(n => n.nodeId === entry.nodeId)

    switch (entry.action) {
      case 'added': {
        const data = this.parseNode(entry.data as Record<string, unknown>)
        query.nodeMap.set(entry.nodeId, data)
        if (index >= 0) nodes.splice(index, 1, data)
        else {
          nodes.push(data)
          if (query.totalCount.value !== null) query.totalCount.value++
        }
        break
      }

      case 'updated': {
        const data = this.parseNode(entry.data as Record<string, unknown>)
        query.nodeMap.set(entry.nodeId, data)
        if (index >= 0) nodes.splice(index, 1, data)
        break
      }

      case 'removed': {
        query.nodeMap.delete(entry.nodeId)
        if (index >= 0) nodes.splice(index, 1)
        if (query.totalCount.value !== null) query.totalCount.value = Math.max(0, query.totalCount.value - 1)
        break
      }
    }
  }

  private applyTableEntry(query: ActiveTableQuery, entry: LiveQueryBatchEntry): void {
    switch (entry.action) {
      case 'added': {
        const existed = query.rowMap.has(entry.nodeId)
        this.replaceTableRows(query, entry.nodeId, (entry.data as unknown[][]) ?? [])
        if (!existed && query.totalCount.value !== null) query.totalCount.value++
        break
      }

      case 'updated':
        this.replaceTableRows(query, entry.nodeId, (entry.data as unknown[][]) ?? [])
        break

      case 'removed':
        this.removeTableRows(query, entry.nodeId)
        if (query.totalCount.value !== null) query.totalCount.value = Math.max(0, query.totalCount.value - 1)
        break
    }
  }

  private appendTableRow(query: ActiveTableQuery, nodeId: string, values: unknown[]): void {
    const row = { nodeId, values: this.parseValues(values) }
    query.rows.value.push(row)
    const existing = query.rowMap.get(nodeId)
    if (existing) existing.push(row)
    else query.rowMap.set(nodeId, [row])
  }

  private replaceTableRows(query: ActiveTableQuery, nodeId: string, valueRows: unknown[][]): void {
    this.removeTableRows(query, nodeId)
    for (const values of valueRows) {
      this.appendTableRow(query, nodeId, values)
    }
  }

  private removeTableRows(query: ActiveTableQuery, nodeId: string): void {
    if (!query.rowMap.has(nodeId)) return
    query.rows.value = query.rows.value.filter(r => r.nodeId !== nodeId)
    query.rowMap.delete(nodeId)
  }

  private handleError(msg: Record<string, unknown>): void {
    const liveQueryId = msg.liveQueryId as string | undefined
    if (!liveQueryId) return

    const error = { code: msg.code as string, message: msg.message as string }

    const nodeQuery = this.nodeQueries.get(liveQueryId)
    if (nodeQuery) {
      nodeQuery.error.value = error
      nodeQuery.loading.value = false
      return
    }

    const tableQuery = this.tableQueries.get(liveQueryId)
    if (tableQuery) {
      tableQuery.error.value = error
      tableQuery.loading.value = false
    }
  }

  private sortTableRows(query: ActiveTableQuery): void {
    const orderBy = query.orderBy
    if (orderBy.length === 0) return

    const indexes = orderBy.map(o => query.columns.value.indexOf(o.column))

    query.rows.value.sort((a, b) => {
      for (let i = 0; i < orderBy.length; i++) {
        const idx = indexes[i]
        if (idx < 0) continue
        const cmp = this.compareValues(a.values[idx], b.values[idx])
        if (cmp !== 0) return orderBy[i].direction === 'desc' ? -cmp : cmp
      }
      return a.nodeId < b.nodeId ? -1 : a.nodeId > b.nodeId ? 1 : 0
    })
  }

  private parseNode(data: Record<string, unknown>): Record<string, unknown> {
    return parseResponseJson(JSON.stringify(data))
  }

  private parseValues(values: unknown[]): unknown[] {
    return parseResponseJson(JSON.stringify(values)) as unknown[]
  }

  private compareValues(a: unknown, b: unknown): number {
    if (a === b) return 0
    if (a == null) return -1
    if (b == null) return 1
    if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime()
    if (Decimal.isDecimal(a) && Decimal.isDecimal(b)) return a.comparedTo(b)
    if (typeof a === 'number' && typeof b === 'number') return a - b
    if (typeof a === 'boolean' && typeof b === 'boolean') return (a ? 1 : 0) - (b ? 1 : 0)
    const as = String(a)
    const bs = String(b)
    return as < bs ? -1 : as > bs ? 1 : 0
  }

  private recreateAll(): void {
    const total = this.nodeQueries.size + this.tableQueries.size
    if (total === 0) return
    log.debug(`[LiveQuery] Recreating ${total} queries after reconnect`)

    for (const query of this.nodeQueries.values()) {
      query.nodes.value = []
      query.loading.value = true
      query.totalCount.value = null
      query.capped.value = false
      query.error.value = null
      query.nodeMap.clear()
      this.client.send({ type: 'createLiveQuery', id: query.id, query: query.spec })
    }

    for (const query of this.tableQueries.values()) {
      query.columns.value = []
      query.rows.value = []
      query.orderBy = []
      query.loading.value = true
      query.totalCount.value = null
      query.capped.value = false
      query.error.value = null
      query.rowMap.clear()
      this.client.send({ type: 'createLiveQuery', id: query.id, query: query.spec, format: 'table' })
    }
  }
}
